import { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { fetchWithAuth } from '@/lib/fetchWithAuth';
import { cn } from '@/lib/utils';
import type { Tarefa, Template } from '@/types/tarefas';
import {
  Plus,
  AlertCircle,
  FileText,
  Trash2,
  Lightbulb,
  X,
  Loader2,
  MoreHorizontal,
  ChevronLeft,
  GripVertical,
} from 'lucide-react';

const BOARD_DROPPABLE_ID = 'board-columns';
const DND_TYPE_BOARD = 'BOARD';
const DND_TYPE_TASK = 'TASK';

type LeadOption = {
  id: string;
  nome: string;
  etapa: string;
  razaoSocial?: string;
  seguirProcessoTarefas?: boolean;
};

/** Valor do Select quando a tarefa não será vinculada a um lead. */
const NO_LEAD_SELECT_VALUE = '__none__';

function defaultDueDateIso(): string {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  d.setHours(18, 0, 0, 0);
  return d.toISOString();
}

export type KanbanColumnId = 'automaticas' | 'pendente' | 'em_andamento' | 'concluidas' | 'canceladas';

const KANBAN_TITLES_STORAGE_KEY = 'sun7-tarefas-kanban-column-titles';

const COLUMNS: { id: KanbanColumnId; title: string; className: string }[] = [
  { id: 'automaticas', title: 'Automáticas (templates)', className: 'bg-amber-50/90 dark:bg-amber-950/30' },
  { id: 'pendente', title: 'Pendente', className: 'bg-muted/60' },
  { id: 'em_andamento', title: 'Em andamento', className: 'bg-sky-50/90 dark:bg-sky-950/25' },
  { id: 'concluidas', title: 'Concluídas', className: 'bg-emerald-50/90 dark:bg-emerald-950/25' },
  { id: 'canceladas', title: 'Canceladas', className: 'bg-slate-100/90 dark:bg-slate-900/40' },
];

const COLUMN_ORDER_STORAGE_KEY = 'sun7-tarefas-kanban-column-order';
const COLUMN_BG_STORAGE_KEY = 'sun7-tarefas-kanban-column-bg';
const COLUMN_SORT_STORAGE_KEY = 'sun7-tarefas-kanban-column-sort';
const CUSTOM_LISTS_STORAGE_KEY = 'sun7-tarefas-kanban-custom-lists';
const TASK_SWIMLANE_STORAGE_KEY = 'sun7-tarefas-kanban-task-swimlane';

/** Lista extra criada pelo usuário (arrastáveis como as colunas fixas). */
export type CustomColumnId = `custom_${string}`;

export type BoardColumnId = KanbanColumnId | CustomColumnId;

export function isCustomListId(id: string): id is CustomColumnId {
  return id.startsWith('custom_');
}

function generateCustomListId(): CustomColumnId {
  return `custom_${crypto.randomUUID()}`;
}

type CustomListMeta = { id: CustomColumnId; title: string };

function readCustomLists(): CustomListMeta[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(CUSTOM_LISTS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    const out: CustomListMeta[] = [];
    for (const item of parsed) {
      if (!item || typeof item !== 'object') continue;
      const rec = item as Record<string, unknown>;
      const id = rec.id;
      const title = rec.title;
      if (typeof id === 'string' && isCustomListId(id) && typeof title === 'string' && title.trim()) {
        out.push({ id, title: title.trim().slice(0, 80) });
      }
    }
    return out;
  } catch {
    return [];
  }
}

function readTaskSwimlaneMap(): Record<string, CustomColumnId> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(TASK_SWIMLANE_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const out: Record<string, CustomColumnId> = {};
    for (const [taskId, v] of Object.entries(parsed)) {
      if (typeof v === 'string' && isCustomListId(v)) {
        out[taskId] = v;
      }
    }
    return out;
  } catch {
    return {};
  }
}

export type ColumnSortKey = 'titulo' | 'prioridade' | 'vencimento' | 'criacao';

const LIST_COLOR_SWATCHES = [
  '#b7e599',
  '#f5e499',
  '#facd9a',
  '#f5b8b8',
  '#d4c4f5',
  '#a8d4f0',
  '#c4e8f5',
  '#d9f2c4',
  '#f5c4e0',
  '#d1d5db',
] as const;

type ListActionsView = 'menu' | 'moveList' | 'moveAll' | 'sort' | 'color';

type OrderedColumnDef = {
  id: BoardColumnId;
  className: string;
};

function readColumnOrder(customIds: CustomColumnId[]): BoardColumnId[] {
  const base = COLUMNS.map((c) => c.id);
  const validExtra = new Set<BoardColumnId>(customIds);
  const fullOrderTail: BoardColumnId[] = [...base, ...customIds];
  if (typeof window === 'undefined') return fullOrderTail;
  try {
    const raw = window.localStorage.getItem(COLUMN_ORDER_STORAGE_KEY);
    if (!raw) return fullOrderTail;
    const arr = JSON.parse(raw) as unknown[];
    if (!Array.isArray(arr)) return fullOrderTail;
    const seen = new Set<string>();
    const out: BoardColumnId[] = [];
    for (const x of arr) {
      if (typeof x !== 'string' || seen.has(x)) continue;
      if (base.includes(x as KanbanColumnId)) {
        seen.add(x);
        out.push(x as KanbanColumnId);
      } else if (validExtra.has(x as BoardColumnId)) {
        seen.add(x);
        out.push(x as BoardColumnId);
      }
    }
    for (const id of fullOrderTail) {
      if (!seen.has(id)) out.push(id);
    }
    return out;
  } catch {
    return fullOrderTail;
  }
}

function readColumnBg(): Partial<Record<string, string>> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(COLUMN_BG_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const out: Partial<Record<string, string>> = {};
    for (const id of Object.keys(parsed)) {
      const v = parsed[id];
      if (typeof v === 'string' && /^#[0-9A-Fa-f]{6}$/.test(v)) {
        out[id] = v;
      }
    }
    return out;
  } catch {
    return {};
  }
}

function readColumnSort(): Record<string, ColumnSortKey> {
  const defaults: Record<KanbanColumnId, ColumnSortKey> = {
    automaticas: 'criacao',
    pendente: 'criacao',
    em_andamento: 'criacao',
    concluidas: 'criacao',
    canceladas: 'criacao',
  };
  if (typeof window === 'undefined') return defaults;
  try {
    const raw = window.localStorage.getItem(COLUMN_SORT_STORAGE_KEY);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const keys: ColumnSortKey[] = ['titulo', 'prioridade', 'vencimento', 'criacao'];
    const out: Record<string, ColumnSortKey> = { ...defaults };
    for (const id of Object.keys(parsed)) {
      const v = parsed[id];
      if (typeof v === 'string' && keys.includes(v as ColumnSortKey)) {
        out[id] = v as ColumnSortKey;
      }
    }
    return out;
  } catch {
    return defaults;
  }
}

function sortTasksInColumn(list: Tarefa[], key: ColumnSortKey): Tarefa[] {
  const copy = [...list];
  const prioOrder: Record<string, number> = { urgente: 4, alta: 3, media: 2, baixa: 1 };
  const prio = (p: string) => prioOrder[p] ?? 0;
  switch (key) {
    case 'titulo':
      return copy.sort((a, b) => a.titulo.localeCompare(b.titulo, 'pt-BR'));
    case 'prioridade':
      return copy.sort((a, b) => prio(b.prioridade) - prio(a.prioridade));
    case 'vencimento':
      return copy.sort((a, b) => {
        const ta = a.data_vencimento ? new Date(a.data_vencimento).getTime() : Number.POSITIVE_INFINITY;
        const tb = b.data_vencimento ? new Date(b.data_vencimento).getTime() : Number.POSITIVE_INFINITY;
        return ta - tb;
      });
    case 'criacao':
    default:
      return copy.sort(
        (a, b) => new Date(b.data_criacao).getTime() - new Date(a.data_criacao).getTime()
      );
  }
}

function defaultColumnTitles(): Record<KanbanColumnId, string> {
  return COLUMNS.reduce<Record<KanbanColumnId, string>>((acc, col) => {
    acc[col.id] = col.title;
    return acc;
  }, {} as Record<KanbanColumnId, string>);
}

function readColumnTitlesFromStorage(): Record<string, string> {
  const defaults = defaultColumnTitles();
  if (typeof window === 'undefined') return defaults;
  try {
    const raw = window.localStorage.getItem(KANBAN_TITLES_STORAGE_KEY);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const out: Record<string, string> = { ...defaults };
    for (const id of Object.keys(parsed)) {
      const v = parsed[id];
      if (typeof v === 'string' && v.trim()) {
        out[id] = v.trim().slice(0, 80);
      }
    }
    return out;
  } catch {
    return defaults;
  }
}

export function getKanbanColumnForTask(t: Tarefa): KanbanColumnId {
  if (t.status === 'cancelada') return 'canceladas';
  if (t.status === 'concluida' || t.status === 'concluida_com_pendencias') return 'concluidas';
  if (t.status === 'em_andamento') return 'em_andamento';
  if (t.template_id && (t.status === 'pendente' || t.status === 'atrasada')) return 'automaticas';
  if (!t.template_id && (t.status === 'pendente' || t.status === 'atrasada')) return 'pendente';
  return 'pendente';
}

function mergeTitlesForCustomLists(
  titles: Record<string, string>,
  lists: CustomListMeta[]
): Record<string, string> {
  const out = { ...titles };
  for (const c of lists) {
    if (!out[c.id]) out[c.id] = c.title;
  }
  return out;
}

function isDropAllowed(tarefa: Tarefa, destCol: BoardColumnId): boolean {
  if (isCustomListId(destCol)) {
    const s = tarefa.status;
    if (s === 'concluida' || s === 'concluida_com_pendencias' || s === 'cancelada') return false;
    return true;
  }
  if (destCol === 'automaticas') return Boolean(tarefa.template_id);
  if (destCol === 'pendente') return !tarefa.template_id;
  return true;
}

function columnToApiStatus(destCol: BoardColumnId): Tarefa['status'] | null {
  if (isCustomListId(destCol)) return null;
  switch (destCol) {
    case 'automaticas':
    case 'pendente':
      return 'pendente';
    case 'em_andamento':
      return 'em_andamento';
    case 'concluidas':
      return 'concluida';
    case 'canceladas':
      return 'cancelada';
    default:
      return null;
  }
}

export interface TarefasKanbanBoardProps {
  tarefas: Tarefa[];
  onStatusChange: (tarefaId: string, status: Tarefa['status']) => void | Promise<void>;
  onOpenTarefa: (tarefa: Tarefa) => void;
  onDelete?: (tarefaId: string) => void;
  onVerLead?: (leadId: string) => void;
  /** Chamado após criar tarefa pelo compositor inline (ex.: recarregar lista). */
  onAfterQuickCreate?: () => void | Promise<void>;
  /** Se passado, substitui o comportamento padrão de criar uma lista extra no quadro. */
  onAddList?: () => void;
}

export function TarefasKanbanBoard({
  tarefas,
  onStatusChange,
  onOpenTarefa,
  onDelete,
  onVerLead,
  onAfterQuickCreate,
  onAddList,
}: TarefasKanbanBoardProps) {
  const { toast } = useToast();
  const [customLists, setCustomLists] = useState<CustomListMeta[]>(() => readCustomLists());
  const [taskSwimlanes, setTaskSwimlanes] = useState<Record<string, CustomColumnId>>(() =>
    readTaskSwimlaneMap()
  );
  const [columnTitles, setColumnTitles] = useState<Record<string, string>>(() =>
    mergeTitlesForCustomLists(readColumnTitlesFromStorage(), readCustomLists())
  );

  const [composerColumnId, setComposerColumnId] = useState<BoardColumnId | null>(null);
  const [cardTitulo, setCardTitulo] = useState('');
  const [cardLeadId, setCardLeadId] = useState('');
  const [cardTemplateId, setCardTemplateId] = useState('');
  const [leads, setLeads] = useState<LeadOption[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [submittingCard, setSubmittingCard] = useState(false);
  const composerTextareaRef = useRef<HTMLTextAreaElement>(null);

  const [editingColumnId, setEditingColumnId] = useState<BoardColumnId | null>(null);
  const [editTitleDraft, setEditTitleDraft] = useState('');

  const [columnOrder, setColumnOrder] = useState<BoardColumnId[]>(() =>
    readColumnOrder(readCustomLists().map((c) => c.id))
  );
  const [columnBg, setColumnBg] = useState<Partial<Record<string, string>>>(() => readColumnBg());
  const [columnSort, setColumnSort] = useState<Record<string, ColumnSortKey>>(() => readColumnSort());

  const [listActionsColumnId, setListActionsColumnId] = useState<BoardColumnId | null>(null);
  const [listActionsView, setListActionsView] = useState<ListActionsView>('menu');
  const [moveListPosition, setMoveListPosition] = useState('1');
  const [moveAllTargetCol, setMoveAllTargetCol] = useState<BoardColumnId | null>(null);
  const [sortDraft, setSortDraft] = useState<ColumnSortKey>('criacao');
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  const openTitleDialog = useCallback(
    (columnId: BoardColumnId) => {
      setEditingColumnId(columnId);
      setEditTitleDraft(columnTitles[columnId] ?? '');
    },
    [columnTitles]
  );

  const closeTitleDialog = useCallback(() => {
    setEditingColumnId(null);
    setEditTitleDraft('');
  }, []);

  const saveColumnTitle = useCallback(() => {
    if (!editingColumnId) return;
    const fallback =
      COLUMNS.find((c) => c.id === editingColumnId)?.title ??
      customLists.find((c) => c.id === editingColumnId)?.title ??
      'Lista';
    const trimmed = editTitleDraft.trim();
    const next = (trimmed || fallback).slice(0, 80);
    if (isCustomListId(editingColumnId)) {
      setCustomLists((prev) => {
        const updated = prev.map((c) => (c.id === editingColumnId ? { ...c, title: next } : c));
        try {
          window.localStorage.setItem(CUSTOM_LISTS_STORAGE_KEY, JSON.stringify(updated));
        } catch {
          /* ignore */
        }
        return updated;
      });
    }
    const updated = { ...columnTitles, [editingColumnId]: next };
    setColumnTitles(updated);
    try {
      window.localStorage.setItem(KANBAN_TITLES_STORAGE_KEY, JSON.stringify(updated));
    } catch {
      /* ignore */
    }
    closeTitleDialog();
    toast({ title: 'Nome da lista atualizado' });
  }, [editingColumnId, editTitleDraft, columnTitles, closeTitleDialog, toast, customLists]);

  const addCustomList = useCallback(() => {
    const id = generateCustomListId();
    const title = 'Nova lista';
    const meta: CustomListMeta = { id, title };
    setCustomLists((prev) => {
      const next = [...prev, meta];
      try {
        window.localStorage.setItem(CUSTOM_LISTS_STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
    setColumnOrder((prev) => {
      const next = [...prev, id];
      try {
        window.localStorage.setItem(COLUMN_ORDER_STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
    setColumnTitles((prev) => {
      const next = { ...prev, [id]: title };
      try {
        window.localStorage.setItem(KANBAN_TITLES_STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
    setColumnSort((prev) => {
      const next = { ...prev, [id]: 'criacao' };
      try {
        window.localStorage.setItem(COLUMN_SORT_STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
    toast({
      title: 'Lista criada',
      description: 'Clique no título para renomear. Arraste cartões para organizar.',
    });
  }, [toast]);

  const handleAddListClick = useCallback(() => {
    if (onAddList) {
      onAddList();
      return;
    }
    addCustomList();
  }, [onAddList, addCustomList]);

  const closeCardComposer = useCallback(() => {
    setComposerColumnId(null);
    setCardTitulo('');
    setCardLeadId('');
    setCardTemplateId('');
    setTemplates([]);
  }, []);

  const openCardComposer = useCallback((columnId: BoardColumnId) => {
    setComposerColumnId(columnId);
    setCardTitulo('');
    setCardLeadId(columnId === 'automaticas' ? '' : NO_LEAD_SELECT_VALUE);
    setCardTemplateId('');
    setTemplates([]);
  }, []);

  useEffect(() => {
    if (!composerColumnId) return;
    let cancelled = false;
    (async () => {
      setLoadingLeads(true);
      try {
        const res = await fetchWithAuth('/api/leads?crm_funil=vendas');
        if (!res.ok) throw new Error('Erro ao carregar leads');
        const data = (await res.json()) as Record<string, unknown>[];
        if (cancelled) return;
        const mapped: LeadOption[] = data.map((row) => ({
          id: String(row.id),
          nome: String(row.nome ?? row.razaoSocial ?? 'Sem nome'),
          etapa: String(row.etapa ?? ''),
          razaoSocial: row.razaoSocial != null ? String(row.razaoSocial) : undefined,
          seguirProcessoTarefas:
            row.seguirProcessoTarefas === undefined ? true : Boolean(row.seguirProcessoTarefas),
        }));
        setLeads(mapped);
      } catch {
        if (!cancelled) {
          setLeads([]);
          toast({
            title: 'Erro',
            description: 'Não foi possível carregar a lista de leads.',
            variant: 'destructive',
          });
        }
      } finally {
        if (!cancelled) setLoadingLeads(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [composerColumnId, toast]);

  useEffect(() => {
    if (
      composerColumnId !== 'automaticas' ||
      !cardLeadId ||
      cardLeadId === NO_LEAD_SELECT_VALUE
    ) {
      setTemplates([]);
      setCardTemplateId('');
      return;
    }
    const lead = leads.find((l) => l.id === cardLeadId);
    if (!lead?.etapa) return;
    let cancelled = false;
    (async () => {
      setLoadingTemplates(true);
      try {
        const params = new URLSearchParams();
        params.set('etapa_funil', lead.etapa);
        params.set('ativo', 'true');
        const res = await fetchWithAuth(`/api/tarefas-templates?${params.toString()}`);
        if (!res.ok) throw new Error('Erro ao carregar templates');
        const data = (await res.json()) as Template[];
        if (!cancelled) setTemplates(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) {
          setTemplates([]);
          toast({
            title: 'Erro',
            description: 'Não foi possível carregar os templates desta etapa.',
            variant: 'destructive',
          });
        }
      } finally {
        if (!cancelled) setLoadingTemplates(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [composerColumnId, cardLeadId, leads, toast]);

  useEffect(() => {
    if (!composerColumnId) return;
    if (composerColumnId === 'automaticas') return;
    const id = requestAnimationFrame(() => {
      composerTextareaRef.current?.focus();
    });
    return () => cancelAnimationFrame(id);
  }, [composerColumnId]);

  const submitCardComposer = useCallback(
    async (columnId: BoardColumnId) => {
      if (submittingCard) return;

      if (columnId === 'automaticas') {
        if (!cardLeadId) {
          toast({
            title: 'Lead obrigatório',
            description: 'Templates de funil precisam estar ligados a um lead.',
            variant: 'destructive',
          });
          return;
        }
        if (!cardTemplateId) {
          toast({
            title: 'Template obrigatório',
            description: 'Selecione um template de tarefa.',
            variant: 'destructive',
          });
          return;
        }
      } else {
        const titulo = cardTitulo.trim();
        if (!titulo) {
          toast({
            title: 'Título obrigatório',
            description: 'Insira um título para a tarefa.',
            variant: 'destructive',
          });
          return;
        }
      }

      const leadIdParaApi =
        cardLeadId && cardLeadId !== NO_LEAD_SELECT_VALUE ? cardLeadId : null;

      if (columnId === 'automaticas') {
        const selectedLead = leads.find((l) => l.id === leadIdParaApi);
        if (!selectedLead) {
          toast({ title: 'Lead inválido', variant: 'destructive' });
          return;
        }
      } else if (leadIdParaApi) {
        const selectedLead = leads.find((l) => l.id === leadIdParaApi);
        if (!selectedLead) {
          toast({ title: 'Lead inválido', variant: 'destructive' });
          return;
        }
        if (selectedLead.seguirProcessoTarefas !== false) {
          toast({
            title: 'Funil automático ativo',
            description:
              'Tarefas avulsas só podem ser criadas para leads com o processo de tarefas do funil desligado na ficha do cliente. Deixe o lead em branco para uma tarefa sem vínculo ou use a coluna Automáticas (templates).',
            variant: 'destructive',
          });
          return;
        }
      }

      setSubmittingCard(true);
      try {
        if (columnId === 'automaticas') {
          const res = await fetchWithAuth('/api/tarefas', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ lead_id: leadIdParaApi, template_id: cardTemplateId }),
          });
          const body = await res.json().catch(() => ({}));
          if (!res.ok) {
            throw new Error((body as { error?: string }).error || 'Erro ao criar tarefa');
          }
        } else {
          const res = await fetchWithAuth('/api/tarefas', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              sem_template: true,
              ...(leadIdParaApi ? { lead_id: leadIdParaApi } : {}),
              titulo: cardTitulo.trim(),
              data_vencimento: defaultDueDateIso(),
            }),
          });
          const body = await res.json().catch(() => ({}));
          if (!res.ok) {
            throw new Error((body as { error?: string }).error || 'Erro ao criar tarefa');
          }
          const created = body as { id?: string };
          if (!created.id) throw new Error('Resposta sem id da tarefa');

          if (isCustomListId(columnId)) {
            setTaskSwimlanes((prev) => {
              const next = { ...prev, [created.id!]: columnId };
              try {
                window.localStorage.setItem(TASK_SWIMLANE_STORAGE_KEY, JSON.stringify(next));
              } catch {
                /* ignore */
              }
              return next;
            });
          } else if (columnId === 'em_andamento') {
            await onStatusChange(created.id, 'em_andamento');
          } else if (columnId === 'concluidas') {
            await onStatusChange(created.id, 'concluida');
          } else if (columnId === 'canceladas') {
            await onStatusChange(created.id, 'cancelada');
          }
        }

        toast({ title: 'Cartão adicionado' });
        closeCardComposer();
        await onAfterQuickCreate?.();
      } catch (e) {
        toast({
          title: 'Não foi possível adicionar',
          description: e instanceof Error ? e.message : 'Erro desconhecido',
          variant: 'destructive',
        });
      } finally {
        setSubmittingCard(false);
      }
    },
    [
      submittingCard,
      cardLeadId,
      cardTitulo,
      cardTemplateId,
      leads,
      onStatusChange,
      onAfterQuickCreate,
      closeCardComposer,
      toast,
    ]
  );

  const customIdSet = useMemo(() => new Set(customLists.map((c) => c.id)), [customLists]);

  const orderedColumns = useMemo((): OrderedColumnDef[] => {
    const fixedIds = new Set(COLUMNS.map((c) => c.id));
    return columnOrder
      .filter((id) => fixedIds.has(id as KanbanColumnId) || customIdSet.has(id as CustomColumnId))
      .map((id) => {
        const fixed = COLUMNS.find((c) => c.id === id);
        if (fixed) {
          return { id: fixed.id, className: fixed.className };
        }
        return {
          id,
          className: 'bg-muted/40 dark:bg-muted/20',
        };
      });
  }, [columnOrder, customIdSet]);

  const byColumn = useMemo(() => {
    const fixedIds = new Set(COLUMNS.map((c) => c.id));
    const orderedIds = columnOrder.filter(
      (id) => fixedIds.has(id as KanbanColumnId) || customIdSet.has(id as CustomColumnId)
    );
    const map: Record<string, Tarefa[]> = {};
    for (const id of orderedIds) {
      map[id] = [];
    }
    for (const t of tarefas) {
      const swim = taskSwimlanes[t.id];
      if (swim && customIdSet.has(swim) && map[swim]) {
        map[swim].push(t);
        continue;
      }
      const base = getKanbanColumnForTask(t);
      if (map[base]) map[base].push(t);
    }
    for (const id of orderedIds) {
      map[id] = sortTasksInColumn(map[id], columnSort[id] ?? 'criacao');
    }
    return map;
  }, [tarefas, columnSort, columnOrder, taskSwimlanes, customIdSet]);

  useEffect(() => {
    const taskIds = new Set(tarefas.map((t) => t.id));
    const validCustom = new Set(customLists.map((c) => c.id));
    setTaskSwimlanes((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const tid of Object.keys(next)) {
        if (!taskIds.has(tid) || !validCustom.has(next[tid])) {
          delete next[tid];
          changed = true;
        }
      }
      if (!changed) return prev;
      try {
        window.localStorage.setItem(TASK_SWIMLANE_STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, [tarefas, customLists]);

  const closeListActionsDialog = useCallback(() => {
    setListActionsColumnId(null);
    setListActionsView('menu');
    setMoveAllTargetCol(null);
  }, []);

  const openListActionsMenu = useCallback((columnId: BoardColumnId) => {
    setListActionsColumnId(columnId);
    setListActionsView('menu');
    const pos = columnOrder.indexOf(columnId);
    setMoveListPosition(String(pos >= 0 ? pos + 1 : 1));
    setSortDraft(columnSort[columnId] ?? 'criacao');
    setMoveAllTargetCol(null);
  }, [columnOrder, columnSort]);

  const persistColumnOrder = useCallback((order: BoardColumnId[]) => {
    setColumnOrder(order);
    try {
      window.localStorage.setItem(COLUMN_ORDER_STORAGE_KEY, JSON.stringify(order));
    } catch {
      /* ignore */
    }
  }, []);

  const persistColumnBg = useCallback((bg: Partial<Record<string, string>>) => {
    setColumnBg(bg);
    try {
      window.localStorage.setItem(COLUMN_BG_STORAGE_KEY, JSON.stringify(bg));
    } catch {
      /* ignore */
    }
  }, []);

  const persistColumnSort = useCallback((sort: Record<string, ColumnSortKey>) => {
    setColumnSort(sort);
    try {
      window.localStorage.setItem(COLUMN_SORT_STORAGE_KEY, JSON.stringify(sort));
    } catch {
      /* ignore */
    }
  }, []);

  const handleCopyList = useCallback(
    async (columnId: BoardColumnId) => {
      const title = columnTitles[columnId] ?? 'Lista';
      const lines = (byColumn[columnId] ?? []).map(
        (t) => `- ${t.titulo}${t.lead?.nome ? ` (${t.lead.nome})` : ''}`
      );
      const text = [`Lista: ${title}`, '', ...lines].join('\n');
      try {
        await navigator.clipboard.writeText(text);
        toast({ title: 'Lista copiada', description: 'Conteúdo copiado para a área de transferência.' });
      } catch {
        toast({
          title: 'Não foi possível copiar',
          description: 'Permita acesso à área de transferência ou copie manualmente.',
          variant: 'destructive',
        });
      }
      closeListActionsDialog();
    },
    [columnTitles, byColumn, toast, closeListActionsDialog]
  );

  const handleApplyMoveList = useCallback(() => {
    if (!listActionsColumnId) return;
    const pos = Number.parseInt(moveListPosition, 10);
    if (Number.isNaN(pos) || pos < 1 || pos > columnOrder.length) {
      toast({ title: 'Posição inválida', variant: 'destructive' });
      return;
    }
    const col = listActionsColumnId;
    const next = columnOrder.filter((id) => id !== col);
    next.splice(pos - 1, 0, col);
    persistColumnOrder(next);
    toast({ title: 'Lista movida' });
    closeListActionsDialog();
  }, [listActionsColumnId, moveListPosition, columnOrder, persistColumnOrder, toast, closeListActionsDialog]);

  const handleApplyMoveAllCards = useCallback(async () => {
    if (!listActionsColumnId || !moveAllTargetCol || listActionsColumnId === moveAllTargetCol) {
      toast({ title: 'Selecione outra coluna', variant: 'destructive' });
      return;
    }
    const fromCol = listActionsColumnId;
    const toCol = moveAllTargetCol;
    const tasks = [...(byColumn[fromCol] ?? [])];
    if (tasks.length === 0) {
      toast({ title: 'Lista vazia', description: 'Não há cartões para mover.' });
      closeListActionsDialog();
      return;
    }

    if (isCustomListId(toCol)) {
      setBulkActionLoading(true);
      let moved = 0;
      let skipped = 0;
      try {
        const nextSwim = { ...taskSwimlanes };
        for (const t of tasks) {
          if (!isDropAllowed(t, toCol)) {
            skipped += 1;
            continue;
          }
          nextSwim[t.id] = toCol;
          moved += 1;
        }
        setTaskSwimlanes(nextSwim);
        try {
          window.localStorage.setItem(TASK_SWIMLANE_STORAGE_KEY, JSON.stringify(nextSwim));
        } catch {
          /* ignore */
        }
        toast({
          title: 'Cartões movidos',
          description:
            skipped > 0
              ? `${moved} organizados na lista. ${skipped} ignorados (cartões concluídos/cancelados).`
              : `${moved} cartão(ões) em "${columnTitles[toCol] ?? 'lista'}".`,
        });
        closeListActionsDialog();
      } finally {
        setBulkActionLoading(false);
      }
      return;
    }

    const targetStatus = columnToApiStatus(toCol);
    if (!targetStatus) {
      toast({ title: 'Coluna inválida', variant: 'destructive' });
      return;
    }
    setBulkActionLoading(true);
    let moved = 0;
    let skipped = 0;
    const idsToClearSwim: string[] = [];
    try {
      for (const t of tasks) {
        if (!isDropAllowed(t, toCol)) {
          skipped += 1;
          continue;
        }
        await onStatusChange(t.id, targetStatus);
        idsToClearSwim.push(t.id);
        moved += 1;
      }
      if (idsToClearSwim.length > 0) {
        setTaskSwimlanes((prev) => {
          const next = { ...prev };
          for (const id of idsToClearSwim) delete next[id];
          try {
            window.localStorage.setItem(TASK_SWIMLANE_STORAGE_KEY, JSON.stringify(next));
          } catch {
            /* ignore */
          }
          return next;
        });
      }
      await onAfterQuickCreate?.();
      toast({
        title: 'Cartões movidos',
        description:
          skipped > 0
            ? `${moved} movidos. ${skipped} ignorados (regras do quadro: template / coluna Pendente).`
            : `${moved} cartão(ões) enviados para "${columnTitles[toCol]}".`,
      });
      closeListActionsDialog();
    } catch {
      toast({ title: 'Erro ao mover cartões', variant: 'destructive' });
    } finally {
      setBulkActionLoading(false);
    }
  }, [
    listActionsColumnId,
    moveAllTargetCol,
    byColumn,
    taskSwimlanes,
    onStatusChange,
    onAfterQuickCreate,
    toast,
    closeListActionsDialog,
    columnTitles,
  ]);

  const handleApplySort = useCallback(() => {
    if (!listActionsColumnId) return;
    persistColumnSort({ ...columnSort, [listActionsColumnId]: sortDraft });
    toast({ title: 'Ordenação atualizada' });
    closeListActionsDialog();
  }, [listActionsColumnId, sortDraft, columnSort, persistColumnSort, toast, closeListActionsDialog]);

  const handleApplyColor = useCallback(
    (hex: string) => {
      if (!listActionsColumnId) return;
      persistColumnBg({ ...columnBg, [listActionsColumnId]: hex });
      toast({ title: 'Cor da lista atualizada' });
      closeListActionsDialog();
    },
    [listActionsColumnId, columnBg, persistColumnBg, toast, closeListActionsDialog]
  );

  const handleRemoveListColor = useCallback(() => {
    if (!listActionsColumnId) return;
    const next = { ...columnBg };
    delete next[listActionsColumnId];
    persistColumnBg(next);
    toast({ title: 'Cor removida' });
    closeListActionsDialog();
  }, [listActionsColumnId, columnBg, persistColumnBg, toast, closeListActionsDialog]);

  const handleDeleteCustomList = useCallback(() => {
    const id = listActionsColumnId;
    if (!id || !isCustomListId(id)) return;
    setCustomLists((prev) => {
      const next = prev.filter((c) => c.id !== id);
      try {
        window.localStorage.setItem(CUSTOM_LISTS_STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
    setColumnOrder((prev) => {
      const next = prev.filter((c) => c !== id);
      try {
        window.localStorage.setItem(COLUMN_ORDER_STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
    setColumnBg((prev) => {
      const next = { ...prev };
      delete next[id];
      try {
        window.localStorage.setItem(COLUMN_BG_STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
    setColumnSort((prev) => {
      const next = { ...prev };
      delete next[id];
      try {
        window.localStorage.setItem(COLUMN_SORT_STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
    setColumnTitles((prev) => {
      const next = { ...prev };
      delete next[id];
      try {
        window.localStorage.setItem(KANBAN_TITLES_STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
    setTaskSwimlanes((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const tid of Object.keys(next)) {
        if (next[tid] === id) {
          delete next[tid];
          changed = true;
        }
      }
      if (!changed) return prev;
      try {
        window.localStorage.setItem(TASK_SWIMLANE_STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
    closeListActionsDialog();
    toast({
      title: 'Lista excluída',
      description: 'Os cartões voltam para as colunas padrão conforme o status.',
    });
  }, [listActionsColumnId, closeListActionsDialog, toast]);

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      const { destination, source, draggableId, type } = result;
      if (!destination) return;
      if (destination.droppableId === source.droppableId && destination.index === source.index) {
        return;
      }

      if (type === DND_TYPE_BOARD) {
        const next = [...columnOrder];
        const [removed] = next.splice(source.index, 1);
        next.splice(destination.index, 0, removed);
        persistColumnOrder(next);
        return;
      }

      if (type !== DND_TYPE_TASK) return;

      const tarefa = tarefas.find((t) => t.id === draggableId);
      if (!tarefa) return;

      const destCol = destination.droppableId as BoardColumnId;

      if (isCustomListId(destCol)) {
        if (!isDropAllowed(tarefa, destCol)) {
          toast({
            title: 'Movimento não permitido',
            description: 'Cartões concluídos ou cancelados não podem ir para listas personalizadas.',
            variant: 'destructive',
          });
          return;
        }
        if (source.droppableId === destination.droppableId) {
          return;
        }
        setTaskSwimlanes((prev) => {
          const next = { ...prev, [tarefa.id]: destCol };
          try {
            window.localStorage.setItem(TASK_SWIMLANE_STORAGE_KEY, JSON.stringify(next));
          } catch {
            /* ignore */
          }
          return next;
        });
        return;
      }

      if (!isDropAllowed(tarefa, destCol)) {
        toast({
          title: 'Movimento não permitido',
          description:
            destCol === 'automaticas'
              ? 'Só tarefas criadas a partir de template podem ficar nesta coluna enquanto estão pendentes.'
              : 'Tarefas de template não entram na coluna Pendente (avulsas). Use Automáticas ou Em andamento.',
          variant: 'destructive',
        });
        return;
      }

      if (source.droppableId === destination.droppableId) {
        return;
      }

      const hadSwim = taskSwimlanes[tarefa.id] !== undefined;
      if (hadSwim) {
        setTaskSwimlanes((prev) => {
          const next = { ...prev };
          delete next[tarefa.id];
          try {
            window.localStorage.setItem(TASK_SWIMLANE_STORAGE_KEY, JSON.stringify(next));
          } catch {
            /* ignore */
          }
          return next;
        });
      }

      const newStatus = columnToApiStatus(destCol);
      if (!newStatus) return;

      if (newStatus === tarefa.status) {
        if (!hadSwim && getKanbanColumnForTask(tarefa) === destCol) return;
        return;
      }

      void onStatusChange(tarefa.id, newStatus);
    },
    [columnOrder, persistColumnOrder, tarefas, onStatusChange, toast, taskSwimlanes]
  );

  return (
    <>
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="flex gap-3 overflow-x-auto pb-4 scroll-smooth -mx-1 px-1 items-start">
        <Droppable droppableId={BOARD_DROPPABLE_ID} direction="horizontal" type={DND_TYPE_BOARD}>
          {(boardProvided) => (
            <div
              ref={boardProvided.innerRef}
              {...boardProvided.droppableProps}
              className="flex gap-3 flex-nowrap min-w-0"
            >
              {orderedColumns.map((col, colIndex) => (
                <Draggable
                  key={col.id}
                  draggableId={`column-${col.id}`}
                  index={colIndex}
                >
                  {(colProvided, colSnapshot) => (
                    <div
                      ref={colProvided.innerRef}
                      {...colProvided.draggableProps}
                      style={{
                        ...(columnBg[col.id] ? { backgroundColor: columnBg[col.id] } : undefined),
                        ...colProvided.draggableProps.style,
                      }}
                      className={cn(
                        'rounded-lg p-2 min-h-[min(70vh,520px)] w-[min(100vw-2rem,280px)] sm:w-[260px] flex-shrink-0 flex flex-col border border-border/60 transition-shadow',
                        !columnBg[col.id] && col.className,
                        colSnapshot.isDragging && 'ring-2 ring-primary shadow-lg z-10 opacity-95'
                      )}
                    >
                      <div className="flex items-start justify-between gap-1 mb-2 px-1">
                        <div className="flex items-start gap-0.5 min-w-0 flex-1">
                          <button
                            type="button"
                            {...colProvided.dragHandleProps}
                            className="cursor-grab active:cursor-grabbing touch-none shrink-0 rounded-md p-1 -ml-0.5 border border-transparent hover:border-border/40 hover:bg-background/50 text-muted-foreground hover:text-foreground"
                            aria-label="Arrastar para reordenar a lista no quadro"
                          >
                            <GripVertical className="h-4 w-4" aria-hidden />
                          </button>
                          <button
                            type="button"
                            onClick={() => openTitleDialog(col.id)}
                            className={cn(
                              'font-semibold text-sm text-foreground leading-tight text-left rounded-md px-1 py-0.5',
                              'hover:bg-background/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring min-w-0 flex-1'
                            )}
                            title="Clique para editar o nome da lista"
                          >
                            <span className="line-clamp-2 break-words">{columnTitles[col.id]}</span>
                          </button>
                        </div>
                        <div className="flex items-center gap-0.5 shrink-0">
                          <Badge variant="secondary" className="text-[10px] h-6 px-1.5">
                            {byColumn[col.id].length}
                          </Badge>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-muted-foreground hover:text-foreground"
                            aria-label="Ações da lista"
                            onClick={() => openListActionsMenu(col.id)}
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <Droppable droppableId={col.id} type={DND_TYPE_TASK}>
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className="flex-1 min-h-[200px] max-h-[min(60vh,480px)] overflow-y-auto rounded-md border border-dashed border-border/50 p-1.5 space-y-2"
                          >
                            {byColumn[col.id].length === 0 ? (
                              <p className="text-center text-muted-foreground text-xs py-8 px-1">
                                Nenhuma tarefa
                              </p>
                            ) : (
                              byColumn[col.id].map((tarefa, index) => (
                                <Draggable key={tarefa.id} draggableId={tarefa.id} index={index}>
                                  {(dragProvided, snapshot) => (
                                    <div
                                      ref={dragProvided.innerRef}
                                      {...dragProvided.draggableProps}
                                      {...dragProvided.dragHandleProps}
                                    >
                                      <KanbanTaskCard
                                        tarefa={tarefa}
                                        isDragging={snapshot.isDragging}
                                        onOpen={() => onOpenTarefa(tarefa)}
                                        onDelete={onDelete}
                                        onVerLead={onVerLead}
                                      />
                                    </div>
                                  )}
                                </Draggable>
                              ))
                            )}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                      <div className="mt-2 shrink-0">
              {composerColumnId === col.id ? (
                <div className="rounded-md border border-primary/25 bg-card p-2 shadow-sm space-y-2">
                  {col.id !== 'automaticas' && (
                    <Textarea
                      ref={composerTextareaRef}
                      value={cardTitulo}
                      onChange={(e) => setCardTitulo(e.target.value)}
                      placeholder="Insira um título ou cole um link"
                      className="min-h-[72px] resize-none text-sm"
                      disabled={submittingCard}
                    />
                  )}
                  {col.id === 'automaticas' && (
                    <p className="text-xs text-muted-foreground">
                      Escolha o lead e o template. O título da tarefa será o definido no template.
                    </p>
                  )}
                  <div className="space-y-1.5">
                    <Label className="text-xs">
                      {col.id === 'automaticas' ? 'Lead' : 'Lead (opcional)'}
                    </Label>
                    <Select
                      value={
                        col.id === 'automaticas'
                          ? cardLeadId || undefined
                          : cardLeadId || NO_LEAD_SELECT_VALUE
                      }
                      onValueChange={(v) => {
                        setCardLeadId(v);
                        setCardTemplateId('');
                      }}
                      disabled={submittingCard || loadingLeads}
                    >
                      <SelectTrigger className="h-9 text-xs">
                        <SelectValue placeholder={loadingLeads ? 'Carregando…' : 'Selecione o lead'} />
                      </SelectTrigger>
                      <SelectContent className="max-h-56">
                        {col.id !== 'automaticas' && (
                          <SelectItem value={NO_LEAD_SELECT_VALUE} className="text-xs text-muted-foreground">
                            Sem lead
                          </SelectItem>
                        )}
                        {leads.map((l) => (
                          <SelectItem key={l.id} value={l.id} className="text-xs">
                            {l.nome}
                            {l.razaoSocial ? ` (${l.razaoSocial})` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {col.id === 'automaticas' && (
                    <div className="space-y-1.5">
                      <Label className="text-xs">Template</Label>
                      <Select
                        value={cardTemplateId}
                        onValueChange={setCardTemplateId}
                        disabled={
                          submittingCard ||
                          loadingTemplates ||
                          !cardLeadId ||
                          cardLeadId === NO_LEAD_SELECT_VALUE
                        }
                      >
                        <SelectTrigger className="h-9 text-xs">
                          <SelectValue
                            placeholder={
                              loadingTemplates
                                ? 'Carregando…'
                                : !cardLeadId || cardLeadId === NO_LEAD_SELECT_VALUE
                                  ? 'Selecione um lead'
                                  : 'Selecione o template'
                            }
                          />
                        </SelectTrigger>
                        <SelectContent className="max-h-56">
                          {templates.map((t) => (
                            <SelectItem key={t.id} value={t.id} className="text-xs">
                              {t.titulo}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="flex flex-wrap items-center gap-2 pt-1">
                    <Button
                      type="button"
                      size="sm"
                      className="text-xs"
                      disabled={submittingCard}
                      onClick={() => void submitCardComposer(col.id)}
                    >
                      {submittingCard ? (
                        <>
                          <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                          Salvando…
                        </>
                      ) : (
                        'Adicionar cartão'
                      )}
                    </Button>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button type="button" variant="secondary" size="sm" className="text-xs gap-1">
                          <Lightbulb className="h-3.5 w-3.5" />
                          Dica
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-72 text-sm" align="start">
                        <p className="font-medium mb-2">Como adicionar cartões</p>
                        <ul className="list-disc pl-4 space-y-1 text-muted-foreground">
                          {col.id === 'automaticas' ? (
                            <>
                              <li>Use lead na etapa correta e um template ativo dessa etapa.</li>
                              <li>Não é possível duplicar o mesmo template para o mesmo lead.</li>
                            </>
                          ) : (
                            <>
                              <li>O título é obrigatório; o lead é opcional.</li>
                              <li>
                                Se escolher um lead, tarefas <strong>avulsas</strong> exigem funil de tarefas{' '}
                                <strong>desligado</strong> na ficha — ou deixe sem lead para tarefa interna.
                              </li>
                              <li>Prazo padrão: 7 dias às 18h (ajustável depois na tarefa).</li>
                            </>
                          )}
                        </ul>
                      </PopoverContent>
                    </Popover>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 ml-auto shrink-0"
                      aria-label="Fechar"
                      disabled={submittingCard}
                      onClick={closeCardComposer}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start text-muted-foreground hover:text-foreground text-xs"
                  onClick={() => openCardComposer(col.id)}
                >
                  <Plus className="h-3.5 w-3.5 mr-1 shrink-0" />
                  Adicionar cartão
                </Button>
              )}
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {boardProvided.placeholder}
            </div>
          )}
        </Droppable>
        <button
          type="button"
          onClick={handleAddListClick}
          className={cn(
            'flex-shrink-0 self-start w-[min(100vw-2rem,280px)] sm:w-[260px]',
            'rounded-lg min-h-[44px] px-3 py-2.5 flex items-center gap-2 text-left text-sm font-medium',
            'bg-foreground/[0.06] hover:bg-foreground/[0.10] dark:bg-white/[0.08] dark:hover:bg-white/[0.12]',
            'text-foreground/80 hover:text-foreground border border-transparent',
            'backdrop-blur-[2px] transition-colors'
          )}
        >
          <Plus className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
          Adicionar outra lista
        </button>
      </div>
    </DragDropContext>

      <Dialog
        open={listActionsColumnId !== null}
        onOpenChange={(open) => {
          if (!open) closeListActionsDialog();
        }}
      >
        <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
          {listActionsColumnId && listActionsView === 'menu' && (
            <>
              <DialogHeader>
                <DialogTitle>Ações da lista</DialogTitle>
                <DialogDescription>{columnTitles[listActionsColumnId] ?? 'Lista'}</DialogDescription>
              </DialogHeader>
              <div className="flex flex-col gap-0.5 py-2">
                <Button
                  type="button"
                  variant="ghost"
                  className="justify-start font-normal"
                  onClick={() => {
                    const id = listActionsColumnId;
                    closeListActionsDialog();
                    openCardComposer(id);
                  }}
                >
                  Adicionar cartão
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="justify-start font-normal"
                  onClick={() => void handleCopyList(listActionsColumnId)}
                >
                  Copiar lista
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="justify-start font-normal"
                  onClick={() => setListActionsView('moveList')}
                >
                  Mover lista
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="justify-start font-normal"
                  onClick={() => setListActionsView('moveAll')}
                >
                  Mover todos os cartões nesta lista
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="justify-start font-normal"
                  onClick={() => setListActionsView('sort')}
                >
                  Ordenar por…
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="justify-start font-normal"
                  onClick={() => setListActionsView('color')}
                >
                  Alterar cor da lista
                </Button>
                {isCustomListId(listActionsColumnId) && (
                  <Button
                    type="button"
                    variant="ghost"
                    className="justify-start font-normal text-destructive hover:text-destructive"
                    onClick={handleDeleteCustomList}
                  >
                    Excluir esta lista
                  </Button>
                )}
              </div>
            </>
          )}

          {listActionsColumnId && listActionsView === 'moveList' && (
            <>
              <DialogHeader>
                <DialogTitle>Mover lista</DialogTitle>
                <DialogDescription>
                  Altera a ordem das colunas (salvo neste navegador). Você também pode arrastar pela faixa no topo de
                  cada lista.
                </DialogDescription>
              </DialogHeader>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="mb-2 -mt-1 w-fit gap-1 px-0"
                onClick={() => setListActionsView('menu')}
              >
                <ChevronLeft className="h-4 w-4" />
                Voltar
              </Button>
              <div className="space-y-3 py-2">
                <Label>Posição da lista &quot;{columnTitles[listActionsColumnId]}&quot;</Label>
                <Select value={moveListPosition} onValueChange={setMoveListPosition}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {columnOrder.map((id, i) => (
                      <SelectItem key={id} value={String(i + 1)}>
                        {i + 1} — {columnTitles[id]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <DialogFooter className="flex-col sm:flex-row gap-2">
                  <Button type="button" variant="outline" onClick={() => setListActionsView('menu')}>
                    Cancelar
                  </Button>
                  <Button type="button" onClick={handleApplyMoveList}>
                    Aplicar
                  </Button>
                </DialogFooter>
              </div>
            </>
          )}

          {listActionsColumnId && listActionsView === 'moveAll' && (
            <>
              <DialogHeader>
                <DialogTitle>Mover todos os cartões</DialogTitle>
                <DialogDescription>
                  Os cartões de &quot;{columnTitles[listActionsColumnId]}&quot; serão atualizados para a coluna de
                  destino, respeitando as mesmas regras do arrastar-e-soltar.
                </DialogDescription>
              </DialogHeader>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="mb-2 -mt-1 w-fit gap-1 px-0"
                onClick={() => setListActionsView('menu')}
              >
                <ChevronLeft className="h-4 w-4" />
                Voltar
              </Button>
              <div className="space-y-3 py-2">
                <Label>Coluna de destino</Label>
                <Select
                  value={moveAllTargetCol ?? undefined}
                  onValueChange={(v) => setMoveAllTargetCol(v as BoardColumnId)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a coluna" />
                  </SelectTrigger>
                  <SelectContent>
                    {columnOrder
                      .filter((id) => id !== listActionsColumnId)
                      .map((id) => (
                        <SelectItem key={id} value={id}>
                          {columnTitles[id]}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <DialogFooter className="flex-col sm:flex-row gap-2">
                  <Button type="button" variant="outline" onClick={() => setListActionsView('menu')}>
                    Cancelar
                  </Button>
                  <Button
                    type="button"
                    disabled={bulkActionLoading || !moveAllTargetCol}
                    onClick={() => void handleApplyMoveAllCards()}
                  >
                    {bulkActionLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Movendo…
                      </>
                    ) : (
                      'Mover todos'
                    )}
                  </Button>
                </DialogFooter>
              </div>
            </>
          )}

          {listActionsColumnId && listActionsView === 'sort' && (
            <>
              <DialogHeader>
                <DialogTitle>Ordenar por</DialogTitle>
                <DialogDescription>Lista: {columnTitles[listActionsColumnId]}</DialogDescription>
              </DialogHeader>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="mb-2 -mt-1 w-fit gap-1 px-0"
                onClick={() => setListActionsView('menu')}
              >
                <ChevronLeft className="h-4 w-4" />
                Voltar
              </Button>
              <RadioGroup
                value={sortDraft}
                onValueChange={(v) => setSortDraft(v as ColumnSortKey)}
                className="grid gap-3 py-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="titulo" id="sort-titulo" />
                  <Label htmlFor="sort-titulo" className="font-normal cursor-pointer">
                    Título (A–Z)
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="prioridade" id="sort-prio" />
                  <Label htmlFor="sort-prio" className="font-normal cursor-pointer">
                    Prioridade (maior primeiro)
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="vencimento" id="sort-venc" />
                  <Label htmlFor="sort-venc" className="font-normal cursor-pointer">
                    Data de vencimento (mais próxima primeiro)
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="criacao" id="sort-criacao" />
                  <Label htmlFor="sort-criacao" className="font-normal cursor-pointer">
                    Data de criação (mais recente primeiro)
                  </Label>
                </div>
              </RadioGroup>
              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={() => setListActionsView('menu')}>
                  Cancelar
                </Button>
                <Button type="button" onClick={handleApplySort}>
                  Aplicar
                </Button>
              </DialogFooter>
            </>
          )}

          {listActionsColumnId && listActionsView === 'color' && (
            <>
              <DialogHeader>
                <DialogTitle>Alterar cor da lista</DialogTitle>
                <DialogDescription>Lista: {columnTitles[listActionsColumnId]}</DialogDescription>
              </DialogHeader>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="mb-2 -mt-1 w-fit gap-1 px-0"
                onClick={() => setListActionsView('menu')}
              >
                <ChevronLeft className="h-4 w-4" />
                Voltar
              </Button>
              <div className="grid grid-cols-5 gap-2 py-2">
                {LIST_COLOR_SWATCHES.map((hex) => (
                  <button
                    key={hex}
                    type="button"
                    className="h-9 w-full rounded-md border-2 border-transparent hover:border-foreground/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    style={{ backgroundColor: hex }}
                    title={hex}
                    aria-label={`Cor ${hex}`}
                    onClick={() => handleApplyColor(hex)}
                  />
                ))}
              </div>
              <Separator className="my-2" />
              <Button type="button" variant="outline" className="w-full gap-2" onClick={handleRemoveListColor}>
                <X className="h-4 w-4" />
                Remover cor
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={editingColumnId !== null} onOpenChange={(open) => !open && closeTitleDialog()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Editar nome da lista</DialogTitle>
            <DialogDescription>
              O nome é salvo neste navegador. As colunas continuam as mesmas; apenas o rótulo exibido muda.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-2 py-2">
            <Label htmlFor="kanban-list-title">Nome da lista</Label>
            <Input
              id="kanban-list-title"
              value={editTitleDraft}
              onChange={(e) => setEditTitleDraft(e.target.value)}
              maxLength={80}
              placeholder="Nome da lista"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  saveColumnTitle();
                }
              }}
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={closeTitleDialog}>
              Cancelar
            </Button>
            <Button type="button" onClick={saveColumnTitle}>
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function KanbanTaskCard({
  tarefa,
  isDragging,
  onOpen,
  onDelete,
  onVerLead,
}: {
  tarefa: Tarefa;
  isDragging: boolean;
  onOpen: () => void;
  onDelete?: (id: string) => void;
  onVerLead?: (leadId: string) => void;
}) {
  const handleKeyOpen = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpen();
    }
  };

  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={handleKeyOpen}
      className={cn(
        'p-2.5 shadow-sm cursor-pointer hover:border-primary/40 transition-colors text-left',
        isDragging && 'ring-2 ring-primary shadow-md',
        tarefa.esta_atrasada && tarefa.status !== 'concluida' && tarefa.status !== 'concluida_com_pendencias'
          ? 'border-red-300/80'
          : 'border-border'
      )}
    >
      <div className="flex items-start justify-between gap-1">
        {tarefa.template_id ? (
          <span className="h-1 w-8 rounded-full bg-emerald-500 shrink-0 mt-1" title="Tarefa de template" />
        ) : (
          <span className="h-1 w-8 rounded-full bg-transparent shrink-0" />
        )}
        {onDelete && (
          <button
            type="button"
            className="p-0.5 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10"
            aria-label="Excluir tarefa"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(tarefa.id);
            }}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <p className="text-sm font-medium text-foreground line-clamp-3 mt-1">{tarefa.titulo}</p>
      {tarefa.lead ? (
        <button
          type="button"
          className="text-xs text-primary hover:underline mt-1 block w-full text-left truncate"
          onClick={(e) => {
            e.stopPropagation();
            onVerLead?.(tarefa.lead!.id);
          }}
        >
          {tarefa.lead.nome}
        </button>
      ) : (
        <p className="text-[11px] text-muted-foreground mt-1">Sem lead</p>
      )}
      <div className="flex flex-wrap items-center gap-1 mt-2">
        {tarefa.esta_atrasada && tarefa.status !== 'concluida' && tarefa.status !== 'concluida_com_pendencias' && (
          <Badge variant="outline" className="text-[10px] h-5 border-red-300 text-red-700 gap-0.5 px-1">
            <AlertCircle className="h-3 w-3" />
            Atrasada
          </Badge>
        )}
        {tarefa.prioridade === 'urgente' && (
          <Badge className="text-[10px] h-5 px-1 bg-red-600">Urgente</Badge>
        )}
        {tarefa.prioridade === 'alta' && (
          <Badge className="text-[10px] h-5 px-1 bg-orange-500">Alta</Badge>
        )}
        <Badge variant="outline" className="text-[10px] h-5 px-1 gap-0.5">
          <FileText className="h-3 w-3" />
          {tarefa.tipo_tarefa}
        </Badge>
      </div>
    </Card>
  );
}
